"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { alpha, styled, useTheme } from "@mui/material/styles";

import {
  AttachFile,
  Clear,
  Close,
  InfoOutlined,
  Refresh,
  Save,
} from "@mui/icons-material";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { useBranch } from "../../../layouts/BranchContext";

/* =========================================================
   SCROLLABLE DIALOG BODY
========================================================= */

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  "&::-webkit-scrollbar": {
    width: "6px",
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor:
      theme.palette.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)",
    borderRadius: "3px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor:
      theme.palette.mode === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
  },
  scrollbarWidth: "thin",
  scrollbarColor: `${
    theme.palette.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)"
  } transparent`,
}));

/* =========================================================
   API
========================================================= */

const API_BASE_URL = "https://wims.revisetoexcel.com/api";

const COURSES_LIST_API = "/course/courses/";
const COURSE_BATCHES_API = (courseId) => `/course/courses/${courseId}/batches/`;
const ASSIGNMENT_SAVE_API = "/assignment/save/";

const api = axios.create({
  baseURL: API_BASE_URL,
});

/* =========================================================
   ACCESS TOKEN
========================================================= */

const getAccessToken = () => {
  if (typeof window === "undefined") return null;

  const keys = ["accessToken", "access_token", "token"];

  for (const key of keys) {
    const localToken = localStorage.getItem(key);

    if (localToken) return localToken;

    const sessionToken = sessionStorage.getItem(key);

    if (sessionToken) return sessionToken;
  }

  return null;
};

api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();

    if (token) {
      config.headers = config.headers || {};

      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/* =========================================================
   USER
========================================================= */

const getStoredUser = () => {
  if (typeof window === "undefined") return null;

  try {
    const storedUser =
      sessionStorage.getItem("user") || localStorage.getItem("user");

    if (!storedUser) return null;

    return JSON.parse(storedUser);
  } catch (error) {
    console.error("User parse error:", error);

    return null;
  }
};

/* =========================================================
   ERROR / RESPONSE HELPERS
========================================================= */

const prettifyFieldLabel = (field) =>
  String(field || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getErrorMessage = (error, fallback = "Something went wrong.") => {
  const data = error?.response?.data;

  if (typeof data === "string" && data.trim()) return data;

  if (data?.detail) return String(data.detail);

  if (data?.message) return String(data.message);

  if (data?.error) return String(data.error);

  if (data && typeof data === "object") {
    const entries = Object.entries(data).filter(
      ([, value]) => value !== null && value !== undefined && value !== ""
    );

    const first = entries[0];

    if (first) {
      const value = Array.isArray(first[1]) ? first[1][0] : first[1];

      if (typeof value === "string") {
        return `${prettifyFieldLabel(first[0])}: ${value}`;
      }
    }
  }

  if (error?.message) return error.message;

  return fallback;
};

const getArray = (response) => {
  const data = response?.data;

  if (Array.isArray(data)) return data;

  if (Array.isArray(data?.data)) return data.data;

  if (Array.isArray(data?.results)) return data.results;

  if (Array.isArray(data?.batches)) return data.batches;

  return [];
};

/* =========================================================
   VALIDATION LIMITS
========================================================= */

const TITLE_MAX = 255;
const DESC_MAX = 1000;
const MAX_FILE_SIZE = 1 * 1024 * 1024; /* 1 MB */

const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getTodayDate = () => formatLocalDate(new Date());

const getDatePlusDays = (days) => {
  const date = new Date();

  date.setDate(date.getDate() + days);

  return formatLocalDate(date);
};

/* =========================================================
   COMPONENT
========================================================= */

function Assignment() {
  const theme = useTheme();

  const isDark = theme.palette.mode === "dark";

  const fileInputRef = useRef(null);

  /* Global branch selector — replaces the local branch
     dropdown and fetchOrganizationBranches call. */

  const { selectedBranchId } = useBranch();

  /* =======================================================
     USER
  ======================================================= */

  const [organizationId, setOrganizationId] = useState("");

  useEffect(() => {
    const user = getStoredUser();

    if (!user) {
      console.warn("Logged-in user not found.");

      return;
    }

    const orgId = user.organization_id || user.organization?.id || user.organization || "";

    setOrganizationId(orgId ? String(orgId) : "");
  }, []);

  /* =======================================================
     FORM STATE
  ======================================================= */
const emptyForm = {
  course: "",
  batch: "",
  teacher: "",
  title: "",
  description: "",
  assignment_start_date: getTodayDate(),
  submission_last_date: getDatePlusDays(10),
};

  const [formData, setFormData] = useState(emptyForm);

  const [file, setFile] = useState(null);

  const [errors, setErrors] = useState({});

  const [saving, setSaving] = useState(false);

  /* =======================================================
     DROPDOWN DATA — all branch-scoped
  ======================================================= */

  const [courses, setCourses] = useState([]);

  const [batches, setBatches] = useState([]);

  const [teachers, setTeachers] = useState([]);

  const [allBatchData, setAllBatchData] = useState([]);

  const [loadingCourses, setLoadingCourses] = useState(false);

  const [loadingBatches, setLoadingBatches] = useState(false);

  const [loadingTeachers, setLoadingTeachers] = useState(false);

  /* =======================================================
     UI STATE
  ======================================================= */

  const [guideOpen, setGuideOpen] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;

    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  /* =======================================================
     FETCH COURSES — branch-scoped
     GET /course/courses/?organization=&branch=
  ======================================================= */

  const fetchCourses = useCallback(async () => {
    if (!organizationId || !selectedBranchId) {
      setCourses([]);

      return;
    }

    setLoadingCourses(true);

    try {
      const response = await api.get(COURSES_LIST_API, {
        params: {
          organization: Number(organizationId),
          branch: Number(selectedBranchId),
        },
      });

      const list = getArray(response).filter(
        (item) => item?.is_active !== false
      );

      setCourses(list);
    } catch (error) {
      console.error("Course fetch error:", error);

      setCourses([]);

      showSnackbar(getErrorMessage(error, "Failed to load courses."), "error");
    } finally {
      setLoadingCourses(false);
    }
  }, [organizationId, selectedBranchId, showSnackbar]);

  /* =======================================================
     FETCH BATCHES — for the selected course
     GET /course/courses/{courseId}/batches/
  ======================================================= */

  const fetchBatches = useCallback(
    async (courseId) => {
      if (!courseId) {
        setBatches([]);

        setAllBatchData([]);

        return;
      }

      setLoadingBatches(true);

      try {
        const response = await api.get(COURSE_BATCHES_API(courseId));

        const list = getArray(response).filter(
          (item) => item?.is_active !== false
        );

        setAllBatchData(list);

        setBatches(list);
      } catch (error) {
        console.error("Batch fetch error:", error);

        setBatches([]);

        setAllBatchData([]);

        showSnackbar(getErrorMessage(error, "Failed to load batches."), "error");
      } finally {
        setLoadingBatches(false);
      }
    },
    [showSnackbar]
  );

  /* Re-fetch the branch-scoped course list whenever the
     global branch changes (also resets downstream). */

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      course: "",
      batch: "",
      teacher: "",
    }));

    setBatches([]);

    setTeachers([]);

    setAllBatchData([]);

    if (organizationId && selectedBranchId) {
      fetchCourses();
    }
  }, [organizationId, selectedBranchId, fetchCourses]);

  /* Auto-select the course when the branch has exactly one. */

  useEffect(() => {
    if (courses.length === 1) {
      setFormData((prev) => ({ ...prev, course: String(courses[0].id) }));
    }
  }, [courses]);

  /* Load batches whenever the course changes. */

  useEffect(() => {
    if (formData.course) {
      fetchBatches(formData.course);
    } else {
      setBatches([]);

      setAllBatchData([]);

      setTeachers([]);
    }

    setFormData((prev) => ({ ...prev, batch: "", teacher: "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.course]);

  /* Auto-select the batch when the course has exactly one. */

  useEffect(() => {
    if (batches.length === 1) {
      setFormData((prev) => ({ ...prev, batch: String(batches[0].id) }));
    }
  }, [batches]);

  /* Trainer — derived from the selected batch's trainer. */

  useEffect(() => {
    if (!formData.batch) {
      setTeachers([]);

      return;
    }

    const selectedBatch = allBatchData.find(
      (item) => String(item.id) === String(formData.batch)
    );

    if (selectedBatch && selectedBatch.trainer) {
      setTeachers([
        {
          id: selectedBatch.trainer,
          name:
            selectedBatch.trainer_name || `Trainer ${selectedBatch.trainer}`,
        },
      ]);
    } else {
      setTeachers([]);
    }
  }, [formData.batch, allBatchData]);

  /* Auto-select the trainer when the batch has exactly one. */

  useEffect(() => {
    if (teachers.length === 1) {
      setFormData((prev) => ({ ...prev, teacher: String(teachers[0].id) }));
    }
  }, [teachers]);

  /* =======================================================
     VALIDATION
  ======================================================= */

  const validateField = (name, value) => {
    switch (name) {
      case "course":
        return value ? "" : "Course is required.";

      case "batch":
        return value ? "" : "Batch is required.";

      case "teacher":
        return value ? "" : "Trainer is required.";

      case "title":
        if (!value.trim()) return "Title is required.";

        if (value.trim().length < 3) return "Title must be at least 3 characters.";

        if (value.length > TITLE_MAX)
          return `Title cannot exceed ${TITLE_MAX} characters.`;

        return "";

      case "description":
        if (!value.trim()) return "Description is required.";

        if (value.trim().length < 10)
          return "Description must be at least 10 characters.";

        if (value.length > DESC_MAX)
          return `Description cannot exceed ${DESC_MAX} characters.`;

        return "";

      case "submission_last_date": {
        if (!value || !formData.assignment_start_date) return "";

        if (value < formData.assignment_start_date)
          return "Last submission date cannot be before the start date.";

        return "";
      }

      default:
        return "";
    }
  };

  const validateAll = () => {
    const newErrors = {};

    Object.keys(formData).forEach((key) => {
      newErrors[key] = validateField(key, formData[key]);
    });

    setErrors(newErrors);

    return !Object.values(newErrors).some(Boolean);
  };

  /* =======================================================
     CHANGE HANDLERS
  ======================================================= */

  const handleFormChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleFileChange = (event) => {
    const selected = event.target.files[0];

    if (!selected) return;

    if (selected.type !== "application/pdf") {
      setErrors((prev) => ({ ...prev, file: "Only PDF files are allowed." }));

      event.target.value = "";

      return;
    }

    if (selected.size > MAX_FILE_SIZE) {
      setErrors((prev) => ({
        ...prev,
        file: "File size exceeds the 1 MB limit.",
      }));

      event.target.value = "";

      return;
    }

    setErrors((prev) => ({ ...prev, file: "" }));

    setFile(selected);
  };

  const clearFile = () => {
    setFile(null);

    setErrors((prev) => ({ ...prev, file: "" }));

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleReset = () => {
    setFormData(emptyForm);

    setFile(null);

    setErrors({});

    if (fileInputRef.current) fileInputRef.current.value = "";

    if (organizationId && selectedBranchId) {
      fetchCourses();
    }
  };

  /* =======================================================
     SAVE
     POST /assignment/save/
     FormData fields: teacher, course, batch, branch,
     organization, assignment_start_date,
     submission_last_date, title, description, file
  ======================================================= */

  const handleSave = async () => {
    if (!organizationId || !selectedBranchId) {
      showSnackbar("Please select a branch first.", "error");

      return;
    }

    if (!validateAll()) {
      showSnackbar("Please fix the errors before submitting.", "error");

      return;
    }

    setSaving(true);

    try {
      const payload = new FormData();

      payload.append("organization", Number(organizationId));

      payload.append("branch", Number(selectedBranchId));

      payload.append("course", Number(formData.course));

      payload.append("batch", Number(formData.batch));

      payload.append("teacher", Number(formData.teacher));

      payload.append("title", formData.title.trim());

      payload.append("description", formData.description.trim());

      if (formData.assignment_start_date) {
        payload.append("assignment_start_date", formData.assignment_start_date);
      }

      if (formData.submission_last_date) {
        payload.append("submission_last_date", formData.submission_last_date);
      }

      if (file) payload.append("file", file);

      const response = await api.post(ASSIGNMENT_SAVE_API, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data?.success === false) {
        showSnackbar(
          response.data?.message || "Failed to save assignment.",
          "error"
        );

        return;
      }

      showSnackbar("Assignment saved successfully.", "success");

      handleReset();
    } catch (error) {
      console.error("Assignment save error:", error);

      showSnackbar(getErrorMessage(error, "Failed to save assignment."), "error");
    } finally {
      setSaving(false);
    }
  };

  /* =======================================================
     MENU / STYLE HELPERS
  ======================================================= */

  const textPrimary = theme.palette.text.primary;

  const menuProps = {
    PaperProps: {
      sx: {
        maxHeight: 260,
        backgroundColor: theme.palette.background.paper,
        color: textPrimary,
        border: `1px solid ${theme.palette.divider}`,
        "& .MuiMenuItem-root": {
          color: textPrimary,
          "&:hover": { backgroundColor: theme.palette.action.hover },
          "&.Mui-selected": {
            backgroundColor: isDark
              ? "rgba(255,255,255,0.12)"
              : "rgba(0,0,0,0.08)",
          },
        },
        "&::-webkit-scrollbar": { width: "6px" },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)",
          borderRadius: "3px",
        },
      },
    },
  };

  const selectStyles = {
    color: textPrimary,
    backgroundColor: theme.palette.background.paper,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.divider,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "& .MuiSvgIcon-root": { color: textPrimary },
    "& .MuiSelect-select": {
      color: textPrimary,
      WebkitTextFillColor: textPrimary,
    },
    "&.Mui-disabled .MuiSelect-select": {
      color: textPrimary,
      WebkitTextFillColor: textPrimary,
      opacity: 0.6,
    },
  };

  const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
      color: textPrimary,
      backgroundColor: theme.palette.background.paper,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.divider,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "& .MuiInputBase-input": {
      color: textPrimary,
      WebkitTextFillColor: textPrimary,
    },
    "& .MuiInputBase-input::placeholder": {
      color: textPrimary,
      opacity: 0.75,
    },
    "& input[type='date']::-webkit-calendar-picker-indicator": {
      filter: isDark ? "invert(1)" : "none",
      cursor: "pointer",
    },
    "& .MuiInputLabel-root": { color: textPrimary, opacity: 0.85 },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
      opacity: 1,
    },
    "& .MuiFormHelperText-root": { color: textPrimary, opacity: 0.75 },
    "& .MuiFormHelperText-root.Mui-error": {
      color: theme.palette.error.main,
      opacity: 1,
    },
  };

  const labelStyles = {
    color: textPrimary,
    opacity: 0.85,
    "&.Mui-focused": { color: theme.palette.primary.main, opacity: 1 },
    "&.Mui-error": { color: theme.palette.error.main, opacity: 1 },
  };

  const helperTextStyles = {
    "& .MuiFormHelperText-root": { color: textPrimary, opacity: 0.75 },
    "& .MuiFormHelperText-root.Mui-error": {
      color: theme.palette.error.main,
      opacity: 1,
    },
  };

  /* =======================================================
     UI
  ======================================================= */

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        minHeight: "100vh",
        color: theme.palette.text.primary,
      }}
    >
      {/* PAGE HEADER */}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 3,
        }}
      >
<Box
  sx={{
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "flex-end",
    width: "100%",
    gap: 1,
  }}
>
  <Tooltip title="User guide">
    <IconButton
      size="small"
      onClick={() => setGuideOpen(true)}
      sx={{
        color: "#fff",
        backgroundColor: theme.palette.primary.main,
        border: `1px solid ${theme.palette.primary.main}`,
        borderRadius: 1.5,
        width: 36,
        height: 36,
        "&:hover": {
          backgroundColor: theme.palette.primary.dark,
        },
      }}
    >
      <InfoOutlined fontSize="small" />
    </IconButton>
  </Tooltip>
</Box>
      </Box>

      {/* FORM CARD */}

      <Card
        sx={{
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "none",
        }}
      >
        <CardContent sx={{ p: { xs: 2, md: 4 } }}>
          {/* COURSE */}

          <FormControl
            fullWidth
            required
            error={!!errors.course}
            sx={{ mb: 3, ...helperTextStyles }}
                      >
            <InputLabel sx={labelStyles}>Course</InputLabel>

            <Select
              name="course"
              value={formData.course}
              label="Course"
              onChange={handleFormChange}
              disabled={!selectedBranchId || loadingCourses}
              MenuProps={menuProps}
              sx={selectStyles}
            >
              <MenuItem value="">
                {loadingCourses ? "Loading..." : "Select Course"}
              </MenuItem>

              {courses.map((course) => (
                <MenuItem key={course.id} value={course.id}>
                  {course.course_name || `Course ${course.id}`}
                </MenuItem>
              ))}
            </Select>

            <FormHelperText>
              {errors.course ||
                (!selectedBranchId
                  ? "Select a branch first"
                  : "Courses for the selected branch")}
            </FormHelperText>
          </FormControl>

          {/* BATCH */}

          <FormControl
            fullWidth
            required
            error={!!errors.batch}
            sx={{ mb: 3, ...helperTextStyles }}
          >
            <InputLabel sx={labelStyles}>Batch</InputLabel>

            <Select
              name="batch"
              value={formData.batch}
              label="Batch"
              onChange={handleFormChange}
              disabled={!formData.course || loadingBatches}
              MenuProps={menuProps}
              sx={selectStyles}
            >
              <MenuItem value="">
                {loadingBatches ? "Loading..." : "Select Batch"}
              </MenuItem>

              {batches.map((batch) => (
                <MenuItem key={batch.id} value={batch.id}>
                  {batch.batch_name
                    ? `${batch.batch_name}${batch.batch_code ? ` (${batch.batch_code})` : ""}`
                    : batch.batch_code || `Batch ${batch.id}`}
                </MenuItem>
              ))}
            </Select>

            <FormHelperText>
              {errors.batch ||
                (!formData.course ? "Select a course first" : "")}
            </FormHelperText>
          </FormControl>

          {/* TRAINER — derived from the selected batch */}

          <FormControl
            fullWidth
            required
            error={!!errors.teacher}
            sx={{ mb: 3, ...helperTextStyles }}
          >
            <InputLabel sx={labelStyles}>Trainer</InputLabel>

            <Select
              name="teacher"
              value={formData.teacher}
              label="Trainer"
              onChange={handleFormChange}
              disabled={!formData.batch || loadingTeachers}
              MenuProps={menuProps}
              sx={selectStyles}
            >
              <MenuItem value="">Select Trainer</MenuItem>

              {teachers.map((teacher) => (
                <MenuItem key={teacher.id} value={teacher.id}>
                  {teacher.name}
                </MenuItem>
              ))}
            </Select>

            <FormHelperText>
              {errors.teacher ||
                (formData.batch && teachers.length === 0
                  ? "No trainer assigned to this batch"
                  : !formData.batch
                  ? "Select a batch first"
                  : "Trainer from the selected batch")}
            </FormHelperText>
          </FormControl>

          {/* TITLE */}

          <TextField
            fullWidth
            required
            size="small"
            label="Assignment Title"
            name="title"
            value={formData.title}
            onChange={handleFormChange}
            error={!!errors.title}
            helperText={errors.title || `${formData.title.length}/${TITLE_MAX}`}
            inputProps={{ maxLength: TITLE_MAX }}
            sx={{ mb: 3, ...textFieldStyles }}
          />

          {/* DESCRIPTION */}

          <TextField
            fullWidth
            required
            size="small"
            multiline
            rows={4}
            label="Assignment Description"
            name="description"
            value={formData.description}
            onChange={handleFormChange}
            error={!!errors.description}
            helperText={
              errors.description ||
              `${formData.description.length}/${DESC_MAX}`
            }
            inputProps={{ maxLength: DESC_MAX }}
            sx={{ mb: 3, ...textFieldStyles }}
          />

          {/* DATES — per Swagger save body */}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
              mb: 3,
            }}
          >
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Assignment Start Date"
              name="assignment_start_date"
              value={formData.assignment_start_date}
              onChange={handleFormChange}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: getTodayDate() }}
              sx={textFieldStyles}
            />

            <TextField
              fullWidth
              size="small"
              type="date"
              label="Submission Last Date"
              name="submission_last_date"
              value={formData.submission_last_date}
              onChange={handleFormChange}
              error={!!errors.submission_last_date}
              helperText={errors.submission_last_date}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: formData.assignment_start_date || getTodayDate(),
              }}
              sx={textFieldStyles}
            />
          </Box>

          {/* FILE UPLOAD */}

          <Box sx={{ mb: 1, display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              variant="contained"
              component="label"
              startIcon={<AttachFile />}
              sx={{
                borderRadius: "25px",
                height: "40px",
                boxShadow: "none",
                textTransform: "none",
              }}
            >
              Upload PDF

              <input
                type="file"
                hidden
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="application/pdf"
              />
            </Button>

            {file && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Typography variant="body2" sx={{ color: textPrimary }}>
                  {file.name}
                </Typography>

                <IconButton onClick={clearFile} size="small">
                  <Clear fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>

          {errors.file ? (
            <Typography
              variant="caption"
              color="error"
              sx={{ display: "block", mb: 3 }}
            >
              {errors.file}
            </Typography>
          ) : (
            <Typography
              variant="caption"
             sx={{ display: "block", mb: 3, color: textPrimary, opacity: 0.75 }}
            >
              Maximum file size: 1 MB &nbsp;|&nbsp; Accepted format: PDF
            </Typography>
          )}

          <Divider sx={{ mb: 3, ...helperTextStyles }} />

          {/* ACTIONS */}

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
            <Button
              variant="outlined"
              onClick={handleReset}
              disabled={saving}
                            sx={{
                borderRadius: "25px",
                height: "40px",
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                color: textPrimary,
                borderColor: theme.palette.divider,
              }}
            >
              Reset
            </Button>

            <Tooltip
              title={
                !selectedBranchId
                  ? "Select a branch first"
                  : "Fill all required fields to save"
              }
            >
              <span>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving || !selectedBranchId}
                  startIcon={saving ? undefined : <Save />}
                  sx={{
                    borderRadius: "25px",
                    height: "40px",
                    textTransform: "none",
                    fontWeight: 600,
                    px: 3,
                    boxShadow: "none",
                  }}
                >
                  {saving ? (
                    <CircularProgress size={22} color="inherit" />
                  ) : (
                    "Save Assignment"
                  )}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>

      {/* USER GUIDE DIALOG */}

            <Dialog
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.paper,
            color: textPrimary,
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: "#fff",
            fontWeight: 700,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Assignment — User Guide

          <IconButton onClick={() => setGuideOpen(false)} sx={{ color: "#fff" }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <StyledDialogContent dividers sx={{ p: 3 }}>
          <ul style={{ marginLeft: "20px", lineHeight: 2 }}>
            <li>
              Pick the <strong>Branch</strong> from the global branch selector —
              courses, batches and trainers on this page are scoped to it.
            </li>
            <li>
              Select a <strong>Course</strong> belonging to that branch.
            </li>
            <li>
              Select a <strong>Batch</strong> belonging to that course.
            </li>
            <li>
              The <strong>Trainer</strong> is auto-filled from the selected
              batch.
            </li>
            <li>
              Enter the <strong>Title</strong> (3–{TITLE_MAX} characters) and{" "}
              <strong>Description</strong> (10–{DESC_MAX} characters).
            </li>
            <li>
              Optionally set the <strong>Start Date</strong> and{" "}
              <strong>Submission Last Date</strong>.
            </li>
            <li>
              Optionally upload a <strong>PDF</strong> (max 1 MB).
            </li>
            <li>
              Click <strong>Save Assignment</strong> to submit.
            </li>
          </ul>
        </StyledDialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setGuideOpen(false)}
            variant="contained"
            sx={{ borderRadius: "25px", boxShadow: "none", textTransform: "none" }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      {/* SNACKBAR — bottom right */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Assignment;